import { UTCDate } from '@date-fns/utc'
import { Box, ChipProps, IconButton, Tooltip, Typography, TypographyProps, useTheme } from '@mui/material'
import { Variant } from '@mui/material/styles/createTypography'
import { C1Chip } from 'c1ui/components/C1Chip'
import clsx from 'clsx'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { capitalizeFirstLetter } from 'components/no-ui/string-formatting'
import {
    addDays,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    format,
    formatDistance,
    formatDistanceToNow,
    isFuture,
    isPast,
    isToday,
    isYesterday,
    isThisWeek,
    isThisYear,
} from 'date-fns'
import { enUS } from 'date-fns/locale'
import { CancelFunc } from 'lib/cancel'
import { Bell, CircleCheck, Copy, TriangleAlert } from 'lucide-react'
import { Timestamp as pbtsTimestamp, Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useEffect, useReducer } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useStyles } from './styles'

export type TimestampProps = Omit<TypographyProps, 'date' | 'variant'> & {
    date?: Date | pbtsTimestamp
    showCopy?: boolean
    typographyVariant?: Variant
    variant?: DateVariant
    showAbsoluteDateAfterDays?: number
    hideTooltip?: boolean
}

export type DateVariant = 'dense' | 'default' | 'date' | 'date_only' | 'date_only_utc' | 'relative_date_only' | 'period'

export type ExpiredTimeProps = {
    timestamp: pbtsTimestamp
    typographyVariant?: Variant
    component?: React.ElementType
}

// when we upgrade date-dns to v2!
//
// https://date-fns.org/v2.20.0/docs/formatDistanceToNow
//
// // Before v2.0.0
//
// distanceInWordsToNow(new Date(2014, 6, 2), { addSuffix: true })
// //=> 'in 6 months'
//
// // v2.0.0 onward
//
// formatDistanceToNow(new Date(2014, 6, 2), { addSuffix: true })
// //=> 'in 6 months'
export const toRelativeString = (d: Date, options = { addSuffix: true }): string => {
    return formatDistanceToNow(d, options)
}

export const isOlderThanXDays = (date: Date, x: number): boolean => {
    const thirtyDaysAgo = addDays(new Date(), x)
    return isPast(date) && date < thirtyDaysAgo
}

export const toTimeRemaining = (d: Date, intl: IntlShape, options = { addSuffix: false }): string => {
    if (isFuture(d)) {
        return formatDistanceToNow(d, options)
    }
    return intl.formatMessage(
        {
            id: 'components_time_remaining',
            defaultMessage: '{time_remaining} past due',
        },
        {
            time_remaining: formatDistanceToNow(d, options),
        },
    )
}

export const getDateVariantAndValue = (dateLeft: Date, dateRight: Date): { variant: string | null; value: number | null } => {
    let variant: string | null = null
    let value: number | null = null

    const diffDays = Math.abs(differenceInDays(dateLeft, dateRight))
    const diffHours = Math.abs(differenceInHours(dateLeft, dateRight))
    const diffMinutes = Math.abs(differenceInMinutes(dateLeft, dateRight))
    const diffMonths = Math.abs(differenceInMonths(dateLeft, dateRight))

    if (diffMinutes < 1) {
        variant = 'seconds'
    } else if (diffHours < 1) {
        variant = 'minutes'
        value = diffMinutes
    } else if (diffDays < 2) {
        variant = 'hours'
        value = diffHours
    } else if (diffDays < 31) {
        variant = 'days'
        value = diffDays
    } else {
        variant = 'months'
        value = diffMonths
    }

    return { variant, value }
}

export const getTimeAgoString = (date: Date, intl: IntlShape): string => {
    const today = new Date()
    const { variant, value } = getDateVariantAndValue(today, date)
    switch (variant) {
        case 'seconds':
            return intl.formatMessage({
                id: 'time_ago_string_seconds',
                defaultMessage: 'less than 1 minute ago',
            })
        case 'minutes':
            return intl.formatMessage(
                {
                    id: 'time_ago_string_minutes',
                    defaultMessage: '{diff_minutes} minutes ago',
                },
                {
                    diff_minutes: value,
                },
            )
        case 'hours':
            return intl.formatMessage(
                {
                    id: 'time_ago_string_hours',
                    defaultMessage: '{diff_hours} hours ago',
                },
                {
                    diff_hours: value,
                },
            )
        case 'days':
            return intl.formatMessage(
                {
                    id: 'time_ago_string_days',
                    defaultMessage: '{diff_days} days ago',
                },
                {
                    diff_days: value,
                },
            )
        default:
            return intl.formatMessage(
                {
                    id: 'time_ago_string_days',
                    defaultMessage: '{diff_days} months ago',
                },
                {
                    diff_days: value,
                },
            )
    }
}

export const getExpiredString = (date: Date, intl: IntlShape): string => {
    const today = new Date()
    const { variant, value } = getDateVariantAndValue(date, today)

    switch (variant) {
        case 'seconds':
            return intl.formatMessage({
                id: 'expired_string_seconds',
                defaultMessage: 'seconds',
            })
        case 'minutes':
            return intl.formatMessage(
                {
                    id: 'expired_string_minutes',
                    defaultMessage: '{diff_minutes} minutes',
                },
                {
                    diff_minutes: value,
                },
            )
        case 'hours':
            return intl.formatMessage(
                {
                    id: 'expired_string_hours',
                    defaultMessage: '{diff_hours} hours',
                },
                {
                    diff_hours: value,
                },
            )
        case 'days':
            return intl.formatMessage(
                {
                    id: 'expired_string_days',
                    defaultMessage: '{diff_days} days',
                },
                {
                    diff_days: value,
                },
            )
        case 'months':
            return intl.formatMessage(
                {
                    id: 'expired_string_months',
                    defaultMessage: '{diff_months} months',
                },
                {
                    diff_months: value,
                },
            )
        default:
            return getShortString({ d: date, variant: 'date_only' })
    }
}

export const ExpiredTime = ({ timestamp, typographyVariant = 'body1', component = 'div' }: ExpiredTimeProps): React.ReactElement => {
    const intl = useIntl()
    const classes = useStyles()
    const date = pbtsTimestamp.toDate(timestamp)
    return (
        <>
            {isPast(date) ? (
                <C1Icon icon={TriangleAlert} aria-label="expiration-date" className={clsx(classes.icon, classes.error)} />
            ) : null}
            <Typography variant={typographyVariant} component={component}>{getExpiredString(date, intl)}</Typography>
        </>
    )
}

export const ExpiresInTime = ({ timestamp }: ExpiredTimeProps): React.ReactElement => {
    const intl = useIntl()
    const date = pbtsTimestamp.toDate(timestamp)

    return isPast(date) ? <FormattedMessage
        id="date_expires_in"
        description="Displays expires in date for this item"
        defaultMessage="Expired {relative_time} ago"
        values={{
            relative_time: getExpiredString(date, intl),
        }}
    />
        : <FormattedMessage
            id="date_expires_in"
            description="Displays expires in date for this item"
            defaultMessage="Expires in {relative_time}"
            values={{
                relative_time: getExpiredString(date, intl),
            }}
        />
}

export const DueDateString = ({ dueDate, prefix = true }: { dueDate: Date; prefix?: boolean }): React.ReactElement => {
    const theme = useTheme()
    const classes = useStyles(theme)

    const relativeString = toRelativeString(dueDate)
    if (prefix) {
        return (
            <Typography className={isPast(dueDate) ? classes.overdue : undefined}>
                <FormattedMessage
                    id="date_due"
                    description="Displays due date for this item"
                    defaultMessage="Due {relative_time}"
                    values={{
                        relative_time: toRelativeString(dueDate),
                    }}
                />
            </Typography>
        )
    }
    return <Typography>{capitalizeFirstLetter(relativeString)}</Typography>
}

export const DueDateChip = ({ dueDate, size = 'medium' }: { dueDate: Date, size?: ChipProps['size'] }): React.ReactElement => {
    const intl = useIntl()

    const getColor = (): ChipProps['color'] => {
        if (isPast(dueDate)) {
            return 'error'
        }
        if (differenceInDays(dueDate, new Date()) < 7) {
            return 'warning'
        }
        return 'info'
    }

    const label = intl.formatMessage({
        id: 'date_due',
        defaultMessage: 'Due {relative_time}',
    }, { relative_time: toRelativeString(dueDate) })

    return <C1Chip
        size={size}
        color={getColor()}
        icon={isPast(dueDate) ? <C1Icon icon={Bell} /> : undefined}
        label={label}
    />
}

// TODO(pquerna): moved to shared location
export const CopyIcon = ({ data }: { data: string }): React.ReactElement => {
    const intl = useIntl()
    const onclick = (): void => {
        navigator.clipboard.writeText(data)
    }
    return (
        <IconButton
            size="small"
            edge="start"
            onClick={onclick}
            aria-label={intl.formatMessage({ id: 'components_copy_icon_aria_label', defaultMessage: 'Copy to clipboard' })}
        >
            <C1Icon icon={Copy} sx={{ color: 'action.active' }} size="sm" />
        </IconButton>
    )
}

const toPeriodString = (d: Date, options: { locale: Locale }, intl?: IntlShape): string => {
    if (intl) {
        if (isToday(d)) {
            return intl.formatMessage({
                id: 'date_today_at',
                defaultMessage: 'Today at {time}',
            }, {
                time: format(d, 'p', options),
            })
        }
        if (isYesterday(d)) {
            return intl.formatMessage({
                id: 'date_yesterday',
                defaultMessage: 'Yesterday',
            })
        }
    }
    if (isThisWeek(d)) {
        // Show day name for this week (e.g., "Friday", "Thursday")
        return format(d, 'EEEE', options)
    }
    if (isThisYear(d)) {
        // Show "Friday, Aug 5" format for this year
        return format(d, 'EEEE, MMM d', options)
    }
    // Show "Aug 5, 2023" format for other years
    return format(d, 'MMM d, yyyy', options)
}

export const getShortString = ({ d, variant, intl }: { d: Date; variant: DateVariant, intl?: IntlShape }): string => {
    switch (variant) {
        case 'relative_date_only':
            return formatDistance(d, new Date(), { addSuffix: true, locale: enUS })
        case 'dense':
            return format(d, 'PPp', { locale: enUS })
        case 'date':
            return format(d, 'yyyy-MMM-dd HH:mm:ss', { locale: enUS })
        case 'date_only':
            return format(d, 'PP', { locale: enUS })
        case 'date_only_utc':
            return d.toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' })
        case 'period':
            return toPeriodString(d, { locale: enUS }, intl)
        case 'default':
        default:
            return toRelativeString(d)
    }
}

// Render protobuf Timestamp or Date object as a relative string
// See also https://material.io/design/communication/data-formats.html#date-and-time
export const RelativeTimestamp: FC<TimestampProps> = ({
    date,
    showCopy,
    typographyVariant = 'body1',
    variant = 'default',
    showAbsoluteDateAfterDays = 30,
    hideTooltip = false,
    ...rest
}) => {
    const forceUpdate = useReducer(() => ({}), {})[1] as () => void
    const intl = useIntl()

    useEffect((): CancelFunc => {
        const interval = setInterval(forceUpdate, 60000)
        return (): void => {
            clearInterval(interval)
        }
    }, [forceUpdate])

    let d: Date

    if (pbtsTimestamp.is(date)) {
        d = pbtsTimestamp.toDate(date)
    } else if (isDate(date)) {
        d = date
    } else {
        return <C1EmptyIndicator />
    }

    if (isOlderThanXDays(d, -showAbsoluteDateAfterDays) && variant === 'relative_date_only') {
        variant = 'date_only'
    }

    const shortString = getShortString({ d, variant, intl })

    const utcDate = new UTCDate(d)
    const isoString = utcDate.toISOString()

    return (
        <>
            {hideTooltip
                ? <Typography variant={typographyVariant} noWrap {...rest}>
                    {shortString}
                </Typography>
                : <Tooltip
                    placement="bottom"
                    title={<Typography variant="caption" color="info.contrastText">{isoString}</Typography>}
                    enterDelay={1000}
                >
                    <Typography variant={typographyVariant} noWrap {...rest}>
                        {shortString}
                    </Typography>
                </Tooltip>}
            {showCopy ? <CopyIcon data={isoString} /> : <></>}
        </>
    )
}

function isDate(thing?: Date | pbtsTimestamp): thing is Date {
    return thing instanceof Date
}

const UtcIsoDate: FC<{ timestamp: Timestamp }> = ({
    timestamp,
}) => <Typography
    variant="caption"
    color="info.contrastText"
>
    {pbtsTimestamp.toDate(timestamp).toISOString()}
</Typography>

const ExpiresInTimeChipOnly: FC<{ timestamp: Timestamp, showTooltip?: boolean }> = ({
    timestamp,
}) => {
    const date = pbtsTimestamp.toDate(timestamp)

    return <C1Chip
        color={isPast(date) ? 'info' : 'warning'}
        label={<ExpiresInTime timestamp={timestamp} />}
    />
}

export const ExpiresInTimeChip: FC<{ timestamp: Timestamp, showTooltip?: boolean }> = ({
    timestamp,
    showTooltip = false,
}) => (showTooltip ? <Tooltip
    title={<UtcIsoDate timestamp={timestamp} />}
    placement="bottom"
    enterDelay={1000}
>
    <Box>
        <ExpiresInTimeChipOnly timestamp={timestamp} />
    </Box>
</Tooltip> : <ExpiresInTimeChipOnly timestamp={timestamp} />)

// Shows a regular date if not expired or "Expired" chip if expired
export const ExpiresAtChip: FC<{ timestamp: Timestamp }> = ({ timestamp }) => {
    const date = pbtsTimestamp.toDate(timestamp)
    const isExpired = date < new Date()

    return (
        <Tooltip
            title={<UtcIsoDate timestamp={timestamp} />}
            placement="bottom"
            enterDelay={1000}
        >
            <Box>
                {isExpired ? (
                    <C1Chip label="Expired" color="error" />
                ) : (
                    <RelativeTimestamp date={timestamp} />
                )}
            </Box>
        </Tooltip>
    )
}

export const GrantedAtChip: FC<{ timestamp?: Timestamp }> = ({ timestamp }) => {
    if (!timestamp) return null

    const timeString = getShortString({ d: Timestamp.toDate(timestamp), variant: 'default' })
    return <Tooltip title={<Typography><FormattedMessage id="components_time_granted" defaultMessage="Granted {time_string}" values={{ time_string: timeString }} /></Typography>}>
        <Box>
            <C1Chip
                color="success"
                icon={<C1Icon icon={CircleCheck} />}
                label={<FormattedMessage id="components_time_granted" defaultMessage="Granted" />}
            />
        </Box>
    </Tooltip>
}
